import { Injectable } from '@angular/core';

export type ConsoleLogLevel = 'OFF' | 'INFO' | 'DEBUG' | 'ALL';

@Injectable({
  providedIn: 'root',
})
export class LogService {
  private _logLevel: ConsoleLogLevel = 'OFF';

  public static logColor: Record<ConsoleLogLevel, string> = {
    OFF: 'color: #0a0;font-weight:bold;',
    INFO: 'color: #0a0;font-weight:bold;',
    DEBUG: 'color: #0a0;font-weight:bold;',
    ALL: 'color: #0a0;font-weight:bold;',
  };

  private static levelPriority: Record<ConsoleLogLevel, number> = {
    OFF: 0,
    INFO: 1,
    DEBUG: 2,
    ALL: 3,
  };

  constructor() {
    // Inizializza dal valore globale (se già presente)
    const initial = this.parseLevel((window as any).logLevel);
    if (initial) {
      this._logLevel = initial;
    }

    // Proxy per intercettare cambi del valore window.logLevel
    this.setupWindowLogLevelSync();
  }

  /** Converte una stringa in ConsoleLogLevel, se valida */
  private parseLevel(level: any): ConsoleLogLevel | null {
    if (!level) return null;

    const validLevels: ConsoleLogLevel[] = ['OFF', 'INFO', 'DEBUG', 'ALL'];
    return validLevels.includes(level) ? (level as ConsoleLogLevel) : null;
  }

  /** Crea un proxy su window.logLevel per intercettare cambi dalla console */
  private setupWindowLogLevelSync(): void {
    let internalValue = this._logLevel;

    Object.defineProperty(window, 'logLevel', {
      get: () => internalValue,
      set: (value: string) => {
        const parsed = this.parseLevel(value);
        if (parsed) {
          internalValue = parsed;
          this.setLogLevel(parsed); // sincronizza servizio
        } else {
          console.warn(`Valore logLevel non valido: ${value}`);
        }
      },
      configurable: true,
    });
  }

  setLogLevel(level: ConsoleLogLevel): void {
    this._logLevel = level;
    if (LogService.levelPriority[this._logLevel] > 0) {
      console.log('%cCurrent Log Level: ' + level, LogService.logColor[level]);
    }
  }

  getLogLevel(): ConsoleLogLevel {
    return this._logLevel;
  }

  /**
   * Da console puoi fare:
   * window.logLevel = 'OFF'
   * window.logLevel = 'INFO'
   * window.logLevel = 'DEBUG'
   * window.logLevel = 'ALL'
   */
  isLoggable(level: ConsoleLogLevel): boolean {
    const current = LogService.levelPriority[this._logLevel];
    const target = LogService.levelPriority[level];

    if (this._logLevel === 'OFF') return false;
    if (this._logLevel === 'ALL') return true;

    return target <= current;
  }
}
